import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UserDocumentScope } from '../../../../contracts';
import { DOCUMENTS_AND_JOBS_KEY, GET_USER_DOCUMENTS } from '../../../../contracts';
import { invalidateGamifyTrialExtension } from '../../../../data/injected';
import { uploadUserDocument } from '../../../../data/library/user-documents/api';
import { useOptimisticUpload } from '../../../../data/library/user-documents/hooks/useDocumentsAndJobs';

interface UploadParams {
  file: File;
  scope?: UserDocumentScope;
  teamId?: string | null;
  onProgress?: (progress: number) => void;
  /** Optional collection ID to auto-add document to after processing */
  collectionId?: string | null;
  /** When true, skip success/error toasts (used for multi-file uploads that show a summary toast) */
  suppressToast?: boolean;
  /** When true, skip query invalidation in onSuccess (caller handles it, e.g. batch uploads) */
  skipQueryInvalidation?: boolean;
}

const generateClientUploadId = () =>
  typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/**
 * Mutation hook to upload a user document with optimistic updates.
 * Uses client_upload_id for deterministic reconciliation between
 * optimistic entries and server data.
 */
export function useUploadUserDocument() {
  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { addOptimisticJob, updateOptimisticJob, removeOptimisticJob } =
    useOptimisticUpload();

  return useMutation({
    mutationFn: async ({
      file,
      scope = 'user',
      teamId = null,
      onProgress,
      collectionId,
    }: UploadParams) => {
      // Generate a stable client_upload_id for this upload
      const clientUploadId = generateClientUploadId();

      // Add optimistic entry with client_upload_id for reconciliation
      const optimisticId = addOptimisticJob(
        file.name,
        scope,
        teamId,
        clientUploadId,
        collectionId
      );

      try {
        const response = await uploadUserDocument(
          file,
          scope,
          onProgress,
          collectionId,
          clientUploadId
        );
        // Reconcile: match by client_upload_id, not by optimistic job_id
        updateOptimisticJob(optimisticId, response.job_id, clientUploadId);
        return response;
      } catch (error) {
        // Remove optimistic entry on failure
        removeOptimisticJob(optimisticId);
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      if (!variables?.skipQueryInvalidation) {
        void queryClient.invalidateQueries({ queryKey: [GET_USER_DOCUMENTS] });
        void queryClient.invalidateQueries({ queryKey: [DOCUMENTS_AND_JOBS_KEY] });
        void invalidateGamifyTrialExtension();
      }
      if (!variables?.suppressToast) {
        toast({
          title: t('userDocuments.uploadSuccess'),
          description: t('userDocuments.uploadSuccessDescription'),
        });
      }
    },
    onError: (error: Error, variables) => {
      if (!variables?.suppressToast) {
        toast({
          tone: 'destructive',
          title: t('userDocuments.uploadError'),
          description: error.message || t('userDocuments.uploadErrorDescription'),
        });
      }
    },
  });
}
